import React, { useCallback, useMemo, useState } from "react";
import Dropzone from "react-dropzone";
import Modal from "../modal";
import NoteInfo from "../note-info";
import Chip from "../chip";
import { useToaster } from "../../common/contexts/toast";
import { errorMessageOf } from "../../utils";

const extOf = (name = "") => name.split(".").pop().toLowerCase();
const fileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;
const shortName = (name = "") =>
    name.length > 22 ? `${name.slice(0, 10)}…${name.slice(-9)}` : name;

/**
 * Reusable multi-file upload dialog: drop/pick files (removable chips), validate
 * them against `validations`, then submit them all as multipart on confirm.
 * Fully prop-driven so it can be reused anywhere — nothing is hard-coded.
 *
 * @param {boolean}  visible
 * @param {function} onClose
 * @param {function} onUploaded            (res.data) => void — after a successful upload.
 * @param {object}   validations           { supportedFileTypes?: string[] (extensions),
 *                                            maxUploadFileSize?: number (MB per file),
 *                                            maxUploadFileQuantity?: number }
 * @param {function} uploadRequest         (FormData) => Promise<res> — REQUIRED, the upload call.
 * @param {string}   fileFieldName         FormData field name each file is appended under.
 * @param {function} successMessage        (count) => string
 * @param {string}   description           optional paragraph above the note.
 * @param {string}   note                  optional info-note text.
 * @param {string}   selectFileTitle       empty-state prompt text.
 */
const MultipleFileUploadModal = ({
    visible = false,
    onClose,
    onUploaded,
    title = "Select documents",
    description,
    note,
    selectFileTitle = "* Drop or click here to upload your files",
    cancelButtonName = "CANCEL",
    confirmButtonName = "ADD",
    size = "large",
    modalClass = "",
    validations = {},
    uploadRequest,
    fileFieldName = "files",
    successMessage = (count) => `Uploaded ${count} file${count !== 1 ? "s" : ""}.`,
    errorFallback = "Failed to upload the files.",
}) => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const { showToaster } = useToaster();

    const { supportedFileTypes, maxUploadFileSize, maxUploadFileQuantity } = validations;

    const accept = useMemo(
        () => (supportedFileTypes?.length ? supportedFileTypes.map((e) => `.${e}`).join(",") : undefined),
        [supportedFileTypes]
    );

    const handleClose = useCallback(() => {
        setFiles([]);
        onClose && onClose();
    }, [onClose]);

    const addFiles = useCallback((incoming = []) => {
        if (!incoming.length) return;
        setFiles((prev) => {
            const seen = new Set(prev.map(fileKey));
            const accepted = [];
            let rejectedType = false;
            let rejectedSize = false;
            let overflow = false;

            for (const file of incoming) {
                if (supportedFileTypes?.length && !supportedFileTypes.includes(extOf(file.name))) {
                    rejectedType = true;
                    continue;
                }
                if (maxUploadFileSize && file.size / 1024 / 1024 > maxUploadFileSize) {
                    rejectedSize = true;
                    continue;
                }
                if (seen.has(fileKey(file))) continue;
                if (maxUploadFileQuantity && prev.length + accepted.length >= maxUploadFileQuantity) {
                    overflow = true;
                    break;
                }
                seen.add(fileKey(file));
                accepted.push(file);
            }

            if (rejectedType) {
                showToaster && showToaster(
                    "multi-upload-type",
                    `Only ${supportedFileTypes.join(", ").toUpperCase()} files are supported.`,
                    false
                );
            }
            if (rejectedSize) {
                showToaster && showToaster("multi-upload-size", `Each file must be under ${maxUploadFileSize}MB.`, false);
            }
            if (overflow) {
                showToaster && showToaster("multi-upload-max", `You can upload up to ${maxUploadFileQuantity} files at a time.`, false);
            }
            return accepted.length ? [...prev, ...accepted] : prev;
        });
    }, [supportedFileTypes, maxUploadFileSize, maxUploadFileQuantity, showToaster]);

    const removeFile = useCallback((key) => {
        setFiles((prev) => prev.filter((file) => fileKey(file) !== key));
    }, []);

    const handleUpload = useCallback(async () => {
        if (!files.length || uploading || !uploadRequest) return;
        setUploading(true);
        try {
            const formData = new FormData();
            files.forEach((file) => formData.append(fileFieldName, file));
            const res = await uploadRequest(formData);
            showToaster && showToaster("multi-upload-success", successMessage(files.length), true);
            onUploaded && onUploaded(res?.data);
            handleClose();
        } catch (error) {
            showToaster && showToaster("multi-upload-error", errorMessageOf(error, errorFallback), false);
        } finally {
            setUploading(false);
        }
    }, [files, uploading, uploadRequest, fileFieldName, successMessage, onUploaded, handleClose, errorFallback, showToaster]);

    const atMax = maxUploadFileQuantity ? files.length >= maxUploadFileQuantity : false;

    return (
        <Modal
            visible={visible}
            title={title}
            onClose={handleClose}
            onClickCancel={handleClose}
            onClickConfirm={handleUpload}
            cancelButtonName={cancelButtonName}
            confirmButtonName={confirmButtonName}
            confirmButtonDisable={!files.length || uploading}
            confirmButtonLoading={uploading}
            size={size}
            modalClass={`multiple-file-upload-modal ${modalClass}`.trim()}
        >
            <div className="multiple-file-upload-modal-content">
                {description && <p className="multiple-file-upload-description">{description}</p>}
                {note && <NoteInfo withInfoIcon note={note} />}

                <div className="input-file input-file-normal input-multiple-files-with-single-input">
                    <Dropzone onDrop={addFiles} accept={accept} multiple disabled={uploading || atMax}>
                        {({ getRootProps, getInputProps }) => (
                            <div className="input-file-container">
                                <label {...getRootProps()}>
                                    <div className="input-file-label">
                                        <div className="files-container">
                                            {files.length ? (
                                                <div className="files with-data">
                                                    {files.map((file) => {
                                                        const key = fileKey(file);
                                                        return (
                                                            <Chip
                                                                key={key}
                                                                name={shortName(file.name)}
                                                                withCross
                                                                onClickCancel={(event) => {
                                                                    event.stopPropagation();
                                                                    event.preventDefault();
                                                                    removeFile(key);
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="files empty-data">
                                                    <div className="select-file-content">
                                                        <img src="/images/icon/upload-file.svg" alt="upload" className="upload-file-icon" />
                                                        <p className="title">{selectFileTitle}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </label>
                                <input {...getInputProps()} />
                            </div>
                        )}
                    </Dropzone>
                </div>
            </div>
        </Modal>
    );
};

export default MultipleFileUploadModal;
